import {
  ItemTradingController_outlet_v1,
  ItemTradingController_park_v1,
  type ItemTradingDayResource,
  type ItemTradingResource,
} from '@repo/api-client';
import { TableRow } from '@repo/ui/components/table';

import { Screen } from '../back-office/Screen';
import { DayLabel, FlowMeasures, ItemNameCell } from './item-cells';
import { ItemReportControls, ItemReportTable } from './item-report';
import { type ItemReportReads, useItemReport } from './use-item-report';

/**
 * What each thing in the catalogue sold over a period, for a manager:
 * `GET /v1/reports/item-trading-summary` and
 * `GET /v1/reports/outlets/{outletId}/item-trading-summary` (#339,
 * `blossom-rest-api#441`).
 *
 * ## The owner's Items, without cost or margin — by type, not by filter
 *
 * These two reads are the manager-reachable twin of the owner-only
 * `GET /v1/reports/items` pair. They publish every figure the owner's row does,
 * unrenamed, and **leave out `costPaise`, `refundedCostPaise`, `voidedCostPaise`
 * and `marginPaise` from the type itself.** So this screen is written against
 * `ItemTradingResource` from the operation to the cell, and nothing here could
 * render a cost if it tried: there is no field to read.
 *
 * That is why this is a screen of its own rather than Items with a column
 * hidden for a manager. A hidden column is a runtime decision on a payload that
 * still carries the figure; this is a payload that never had it. Everything the
 * two share — the controls, the table's states, and every cell but Margin —
 * lives in `use-item-report.ts`, `item-report.tsx` and `item-cells.tsx`, which
 * are generic over the row or typed off this screen's resource, so the owner's types cannot reach
 * this file through them.
 *
 * Everything `Items.tsx`'s head says about the figures holds here unchanged:
 * the count is a level and is never summed, nothing is netted, the write-offs
 * carry their reasons, and there is no footer row and no CSV.
 *
 * ## Manager only, and why not the owner too
 *
 * The routes admit `OWNER` and `MANAGER`. The route table admits only the
 * manager, so an owner keeps the one entry — Items, which is this and the cost
 * beside it — and nobody is offered two screens answering one question.
 * Outlet staff are refused by the API's role guard before any row is read, and
 * by `route-table.tsx` before anything is asked.
 */

const READS: ItemReportReads<ItemTradingResource> = {
  park: (client, range, signal) =>
    client.request(ItemTradingController_park_v1, { query: range, signal }),
  outlet: (client, outletId, range, signal) =>
    client.request(ItemTradingController_outlet_v1, {
      path: { outletId },
      query: range,
      signal,
    }),
};

/** Items' columns, without Margin. See `Items.tsx` for why they run in this order. */
const COLUMNS = [
  { label: 'Item' },
  { label: 'Sold', numeric: true },
  { label: 'Refunded', numeric: true },
  { label: 'Voided', numeric: true },
  { label: 'Delivered', numeric: true },
  { label: 'Written off', numeric: true },
  { label: 'Counted', numeric: true },
  { label: 'Variance', numeric: true },
] as const;

export function ItemSales() {
  const report = useItemReport(READS);

  return (
    <Screen
      title="Item sales"
      lede="what each thing sold, and what the shelf says"
      description="What every item in the catalogue sold over a period, beside what was refunded, delivered, written off and counted. Nothing is netted: sold and refunded are two figures, and the count is the latest one taken rather than a total of the days."
      aside={<ItemReportControls report={report} />}
    >
      <ItemReportTable
        report={report}
        label="Item sales"
        columns={COLUMNS}
        forbidden={{
          title: 'This account cannot read item sales',
          description:
            'Item sales is read by a manager. If that is what you are, sign out and back in — the role this browser is holding may be out of date.',
        }}
      >
        {(items) => items.map((row) => <ItemRows key={row.catalogueItemId} item={row} />)}
      </ItemReportTable>
    </Screen>
  );
}

/** One item's range row, and its day rows under it — each read off its own entry. */
function ItemRows({ item }: { item: ItemTradingResource }) {
  return (
    <>
      <TableRow>
        <ItemNameCell code={item.code} name={item.name} retired={item.retired} />
        <FlowMeasures figures={item} />
      </TableRow>

      {item.days?.map((entry) => (
        <ItemDayRow key={entry.businessDate} day={entry} />
      ))}
    </>
  );
}

function ItemDayRow({ day }: { day: ItemTradingDayResource }) {
  return (
    <TableRow>
      <DayLabel businessDate={day.businessDate} state={day.state} />
      <FlowMeasures figures={day} />
    </TableRow>
  );
}
